import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, PackageX, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { warehouseApi } from '../services/api';
import { fmtDateTime } from '../utils/time';
import './ZeroSaleBadge.css';

/**
 * «Товар лежит, а продаж нет» — сигнал руководителю.
 *
 * Раньше это была постоянная жёлтая плашка в боковом меню: занимала место в
 * каждом экране и примелькалась (аудит №3, U15). Теперь — колокольчик с числом
 * позиций в шапке меню (и в верхней панели на телефоне); по нажатию — список
 * по клиентам и переход в «Хвосты».
 *
 * Данные считает сторож витрины раз в час, здесь только чтение готовой сводки.
 * Опрос — один на страницу (хук в Layout), колокольчиков может быть два
 * (меню и мобильная панель), они получают одни и те же данные.
 */
export function useZeroSaleAlert() {
  const { hasPermission } = useAuth();
  // Ручка под разделом «Остатки» (warehouse): кому раздел не выдан — не зовём,
  // иначе 403 в консоли на каждой странице.
  const canSee = hasPermission('warehouse');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!canSee) return undefined;
    let cancelled = false;
    let t = null;
    const load = async () => {
      // Вкладка в фоне — не опрашиваем: сигнал всё равно никто не видит.
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await warehouseApi.zeroSaleAlert();
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (cancelled) return;
        setData(null);
        // Сессия кончилась (401) или доступа нет (403) — дальше опрашивать бессмысленно
        // (аудит №3, E13).
        const st = e && e.response && e.response.status;
        if (st === 401 || st === 403) { clearInterval(t); t = null; }
      }
    };
    load();
    t = setInterval(load, 5 * 60 * 1000);   // сторож считает раз в час
    return () => { cancelled = true; if (t) clearInterval(t); };
  }, [canSee]);

  return data && data['позиций'] ? data : null;
}

function ZeroSaleBell({ data, align = 'left', variant = 'sidebar' }) {
  const navigate = useNavigate();
  // «Хвосты» живут под разделом зоны ФБС. Кому туда нельзя — список показываем
  // (знать о простое полезно всем), но кнопки перехода нет.
  const { hasPermission, isInternalStaff, moduleOn } = useAuth();
  // Облако Н3: «Хвосты» — служебный экран full-fix, у склада облака его нет
  const canOpen = isInternalStaff && hasPermission('fbs_zone') && moduleOn('internal_audit');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!data) return null;

  const позиций = data['позиций'];
  const штук = data['штук'];
  const клиенты = data['клиенты'] || [];
  const число = позиций > 99 ? '99+' : String(позиций);

  return (
    <div className={`zs-bell zs-bell--${variant}`} ref={ref}>
      <button
        type="button"
        className={`zs-bell__btn${open ? ' is-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={`Нет продаж: ${позиций} поз., ${штук} шт можно вернуть в продажу`}
        aria-label={`Уведомления: нет продаж по ${позиций} позициям`}
        aria-expanded={open}
      >
        <Bell size={17} />
        <span className="zs-bell__count">{число}</span>
      </button>
      {open && (
        <div className={`zs-panel zs-panel--${align}`} role="dialog" aria-label="Уведомления">
          <div className="zs-panel__head">
            <PackageX size={16} className="zs-panel__icon" />
            <div className="zs-panel__title">Товар лежит, а продаж нет</div>
            <button type="button" className="zs-panel__close" aria-label="Закрыть" onClick={() => setOpen(false)}>
              <X size={15} />
            </button>
          </div>
          <div className="zs-panel__sum">
            Позиций: <b>{позиций}</b>, вернуть в продажу можно <b>{штук}</b> шт.
          </div>
          {клиенты.length > 0 && (
            <ul className="zs-panel__list">
              {клиенты.map((c, i) => (
                <li key={`${c['клиент']}-${i}`}>
                  <span className="zs-panel__client">{c['клиент']}</span>
                  <span className="zs-panel__nums">{c['позиций']} поз. · {c['штук']} шт</span>
                </li>
              ))}
            </ul>
          )}
          {data['обновлено'] && (
            <div className="zs-panel__upd">Данные на {fmtDateTime(data['обновлено'])}</div>
          )}
          {canOpen && (
            <button type="button" className="btn btn-primary btn-sm zs-panel__go"
                    onClick={() => { setOpen(false); navigate('/fbs-tails'); }}>
              Разобрать в «Хвостах»
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ZeroSaleBell;
